import { apiWrapper } from 'ccs-ui-components';
import Pako from 'pako';
import { IGetJobsRequest, IGetJobsResponse, IJobSchedulerRequest, IJobSchedulerSuccessResponse } from '../../_components/scheduler/utilities/TypeDefs';
import { journeyCanvasConstants } from '../../constants/JourneyCanvas';
import { createJourneySchema, createScheduleApiSchema, deleteCompensatoryMapSchema, deleteSchedulerSchema, eventSchemaDetailSchema, getCompensatoryEventMapDetailsSchema, getJourneySchema, journeyListSchema, saveCEMSchema, scheduledJobListApiSchema, updateStatusAPISchema, validateStepSchema } from './SchemaDefs';
import { AllServicesApiResponse, CreateCEMRequest, CreateCEMResponse, CreateJourneyData, EventSchemaDetail, FiltersRequestType, GetCompensatoryEventMapDetails, IRecordModel, JourneyListApiResponse, SearchServiceRequestType, SendForApprovalBody, Service, ValidateStepRequest, ValidateStepResponse, ViewJourneyData } from './TypeDefs';

/**
 * @method getAllServices
 * @description api function to get all the services
 * @param page
 * @param pageSize
 * @param search
 * @returns services
 */
export const getAllServices = async (
  page: number,
  pageSize: number,
  search: string,
  egressEvents?: { name: string, version: number }[],
) => {
  const body: SearchServiceRequestType = { services: [], exactNameMatch: false, status: ['ACTIVE'] };

  if (search) {
    body.services = [
      {
        serviceName: search
      }
    ];
  }

  if (egressEvents) {
    body.associatedEvents = {
      associationType: 'ingress',
      events: egressEvents.map((e) => ({ eventName: e.name, eventVersion: e.version.toString() }))
    };
  }

  const resp = await apiWrapper.post<AllServicesApiResponse>(`${process.env.REACT_APP_API_BASE_URL}/services/search?page=${page}&size=${pageSize}&sort=updatedTimestamp,DESC`, body);

  if (!resp.responseBody?.services) {
    return undefined;
  }

  const { services } = resp.responseBody;

  services.forEach((service) => {
    const hasCompensate = service.serviceExceptions?.some((exception) =>
      exception.actions.some((action) => {
        const lowerAction = action.toLowerCase();
        return lowerAction === 'compensate' || lowerAction === 'compensateandrecover';
      }));
    service.isCompensateAction = !!hasCompensate;
  });

  return services;
};

/**
 * @method createJourney
 * @description api function to create journey
 * @param journeyName
 * @param journeyDescription
 * @param journeyType
 * @param createdBy
 * @returns journeyName and journeyUUID (created journey)
 */
export const createJourney = async (journeyData: CreateJourneyData, clonedJourneyVersion?: number) => {
  const headers = { 'Content-Encoding': 'gzip', 'Content-Type': 'application/json' };
  const baseURL = `${process.env.REACT_APP_API_BASE_URL}/journeys`;
  const url = clonedJourneyVersion !== undefined ? `${baseURL}?clonedJourneyVersion=${encodeURIComponent(clonedJourneyVersion)}` : baseURL;
  const resp = await apiWrapper.post<ViewJourneyData>(url, Pako.gzip(JSON.stringify(journeyData)), headers);

  // Schema validation for createJourney api
  const apiResponse = createJourneySchema.validate(resp.responseBody, {
    abortEarly: true
  });
  if (apiResponse.error) {
    return undefined;
  }

  return resp.responseBody;
};

/**
 * @method getAllJourneys
 * @description api function to get all the journeys
 * @param page
 * @param pageSize
 * @param validFromDate
 * @param validToDate
 * @param searchJourney
 * @param statusFilter
 * @param sortParameter
 * @returns journeys
 */
export const getAllJourneys = async ({
  page,
  pageSize,
  validFromDate,
  validToDate,
  searchJourney,
  statusFilter,
  sortParameter,
  validationName
}: {
  page: number,
  pageSize: number,
  validFromDate: string,
  validToDate: string,
  searchJourney: string,
  statusFilter: string[],
  sortParameter: string,
  validationName: boolean
}) => {
  const allStatus = ['Active', 'Draft', 'Inactive', 'Returned', 'Pending_Approval'];
  const body: FiltersRequestType = {
    statusList: statusFilter && statusFilter.some((status) => status !== undefined) ? statusFilter : allStatus,
    exactNameMatch: validationName
  };

  if (searchJourney) {
    body.journeys = [
      {
        journeyName: searchJourney
      }
    ];
  }

  if (validFromDate) {
    body.fromDate = validFromDate;
  }

  if (validToDate) {
    body.toDate = validToDate;
  }

  const resp = await apiWrapper.post<JourneyListApiResponse>(`${process.env.REACT_APP_API_BASE_URL}/journeys/search?page=${page}&size=${pageSize}&sort=${sortParameter}`, body);

  if (
    Object.keys(resp.responseBody ?? '').length === 0
    && resp.isError === false
  ) {
    return {
      totalJourneysCount: 0,
      journeys: [],
      isError: false,
      statusCode: 200
    };
  }

  // Schema validation for getAllJourneys api
  if (resp.responseBody?.journeyList && resp.responseBody?.journeyList.length > 0) {
    const apiResponse = journeyListSchema.validate(resp.responseBody, {
      abortEarly: true
    });
    if (apiResponse.error) {
      return {
        totalJourneysCount: 0,
        journeys: [],
        isError: true,
        statusCode: 500
      };
    }
  }

  return {
    journeys: resp.responseBody?.journeyList ?? [],
    totalJourneysCount: resp.responseBody?.totalJourneysCount ?? 0,
    isError: resp.isError,
    statusCode: resp.statusCode
  };
};

/**
 * @method getJourney
 * @description api function to get the selected journey
 * @param journey
 * @returns journey
 */
export const getJourney = async (journey: string, version: number): Promise<ViewJourneyData | undefined> => {
  const resp = await apiWrapper.get<ViewJourneyData[]>(`${process.env.REACT_APP_API_BASE_URL}/journeys/${journey}/version/${version}`);
  const responseBody = resp.responseBody ?? [];

  // Schema validation for getJourney api
  const apiResponse = getJourneySchema.validate(resp.responseBody, {
    abortEarly: true
  });

  if (apiResponse.error) {
    return undefined;
  }

  return responseBody.length > 0 ? responseBody[0] : undefined;
};

/**
 * @method getListOFJourneysForParticularJourney
 * @description api function to get all the journey version for any particular journey name
 * @param searchJourney
 * @returns journeys
 */
export const getListOfJourneysForParticularJourney = async (searchJourney: string) => {
  const resp = await apiWrapper.post<JourneyListApiResponse>(`${process.env.REACT_APP_API_BASE_URL}/journeys/search?page=0&size=50&sort=updatedTimestamp,desc`, {
    journeys: [{
      journeyName: searchJourney
    }],
    exactNameMatch: true,
  });

  if (
    Object.keys(resp.responseBody ?? '').length === 0
    && resp.isError === false
  ) {
    return {
      totalJourneysCount: 0,
      journeys: [],
      isError: false
    };
  }

  // Schema validation for getAllJourneys api
  const apiResponse = journeyListSchema.validate(resp.responseBody, {
    abortEarly: true
  });

  if (apiResponse.error) {
    return {
      totalJourneysCount: 0,
      journeys: [],
      isError: true
    };
  }

  return {
    journeys: resp.responseBody?.journeyList ?? [],
    totalJourneysCount: resp.responseBody?.totalJourneysCount ?? 0,
    isError: resp.isError
  };
};

/**
 * @method getAssociatedServices
 * @description api function to get the associated services
 * @param serviceOperation
 * @param service
 * @returns resp (associated services)
 */
export const getAssociatedServices = async (version: number, serviceName: string, ingressEvent: string, ingressEventVersion: number, serviceOp: string) => {
  const service = await getServiceByVersionAndName(version, serviceName);
  if (service) {
    const so = service[0].serviceOperations.find((so) => so.serviceOperation === serviceOp);
    const ingress = so?.ingressEvents.find((ig) => ig.ingressEventName === ingressEvent && Number(ig.ingressEventVersion) === ingressEventVersion);
    const egressArr = ingress?.egressEvents;

    if (!egressArr || egressArr.length === 0) {
      return [];
    }

    return getAllServices(0, journeyCanvasConstants.allServicesPageSize, '', egressArr.map((e) => ({ name: e.egressEventName, version: Number(e.egressEventVersion) })));
  }

  return undefined;
};

export const getServiceByVersionAndName = async (version: number, service: string) => {
  const resp = await apiWrapper.get<Service[]>(`${process.env.REACT_APP_API_BASE_URL}/services/${service}/version/${version}`);

  return resp.responseBody;
};

/**
 * @method saveJourney
 * @description api function to save the journey
 * @param journeyData
 * @returns resp (saved journey)
 */
export const saveJourney = async (journeyData: CreateJourneyData, version: number) => {
  const headers = { 'Content-Encoding': 'gzip', 'Content-Type': 'application/json' };
  const resp = await apiWrapper.put<ViewJourneyData>(`${process.env.REACT_APP_API_BASE_URL}/journeys/${journeyData.journeyName}/version/${version}`, Pako.gzip(JSON.stringify(journeyData)), headers);
  return resp.responseBody;
};

/**
 * @method createCompensatoryEventMap
 * @description api function to create compensatory event map
 * @param compensatoryEventMapData
 * @returns journey
 */
export const createCompensatoryEventMap = async (compensatoryEventMapData: CreateCEMRequest) => {
  const resp = await apiWrapper.post<CreateCEMResponse>(`${process.env.REACT_APP_API_BASE_URL}/journeys/compensatoryEventMap`, compensatoryEventMapData);
  // Schema validation for createCompensatoryEventMap api
  const apiResponse = saveCEMSchema.validate(resp.responseBody, {
    abortEarly: true
  });

  if (apiResponse.error) {
    return undefined;
  }

  return resp.responseBody;
};

/**
 * @method updateCompensatoryEventMap
 * @description api function to update compensatory event map
 * @param compensatoryEventMapData
 * @param cemEventMapName
 * @returns journey
 */
export const updateCompensatoryEventMap = async (compensatoryEventMapData: CreateCEMRequest) => {
  const resp = await apiWrapper.put<CreateCEMResponse>(`${process.env.REACT_APP_API_BASE_URL}/journeys/compensatoryEventMap/${compensatoryEventMapData.compensatoryEventMapName}`, compensatoryEventMapData);

  // Schema validation for updateCompensatoryEventMap api
  const apiResponse = saveCEMSchema.validate(resp.responseBody, {
    abortEarly: true
  });

  if (apiResponse.error) {
    return undefined;
  }

  return resp.responseBody;
};

/**
 * @method deleteCompensatoryMap
 * @description api function to delete compensatory event map
 * @param compensatoryNode
 * @returns resp (deleted body)
 */
export const deleteCompensatoryMap = async (journeyName: string, version: number, journeyStepName: string, nodeId: string, cemEventMapName: string) => {
  const resp = await apiWrapper.delete<string>(`${process.env.REACT_APP_API_BASE_URL}/journeys/journey/${journeyName}/version/${version}/journeyStep/${journeyStepName}/node/${nodeId}/compensatoryEventMap/${cemEventMapName}`);
  const apiResponse = deleteCompensatoryMapSchema.validate(
    resp.statusCode,
    { abortEarly: true }
  );

  if (apiResponse.value !== 204) {
    return undefined;
  }

  return resp.responseBody === '';
};

/**
 * @method getCompensateEventMapDetails
 * @description api function to get the compensatory event map details
 * @param cemEventMapName
 * @returns resp (action)
 */
export const getCompensatoryEventMapDetails = async (cemEventMapName: string, version: number) => {
  const resp = await apiWrapper.get<GetCompensatoryEventMapDetails[]>(`${process.env.REACT_APP_API_BASE_URL}/journeys/event-maps/${cemEventMapName}/version/${version}`);
  // Schema validation for getCompensatoryEventMapDetails api
  const apiResponse = getCompensatoryEventMapDetailsSchema.validate(
    resp.responseBody,
    { abortEarly: true }
  );

  if (apiResponse.error) {
    return undefined;
  }
  return resp.responseBody && resp.responseBody.length > 0 ? resp.responseBody[0] : undefined;
};

/**
 * @method exportJourneyConfiguration
 * @param journeyNames
 * @returns resp (action)
 */
export const exportJourneyConfiguration = async (journeyNames: IRecordModel[]) => {
  const { responseBody, request, isError, error } = await apiWrapper.post<ArrayBuffer>(
    `${process.env.REACT_APP_API_BASE_URL}/exim/export`,
    {
      recordType: 'journey',
      records: journeyNames,
    },
    undefined,
    undefined,
    'arraybuffer',
  );

  if (isError) {
    throw error;
  }

  return {
    data: responseBody,
    request
  };
};

export const validateStep = async (
  validateStepRequest: ValidateStepRequest
) => {
  const resp = await apiWrapper.post<
    ValidateStepResponse,
    { errorMessage: string }
  >(
    `${process.env.REACT_APP_API_BASE_URL}/journeys/validateJourneyStep`,
    validateStepRequest,
  );
  if (resp.isError) {
    return {
      isError: true,
      error: resp.errorBody
    };
  }
  const validatorResp = validateStepSchema.validate(resp.responseBody, {
    abortEarly: true
  });
  if (validatorResp.error) {
    return {
      isError: true
    };
  }
  return {
    isError: false,
    resp: resp.responseBody
  };
};

export const createScheduler = async (createScheduleRequest: IJobSchedulerRequest) => {
  const resp = await apiWrapper.post<IJobSchedulerSuccessResponse, { errorMessage: string }>(`${process.env.REACT_APP_API_BASE_URL}/scheduler/schedule-journey`, createScheduleRequest);

  /**
   * @method apiResult
   * @description Validating the api response using joi.
   */
  const apiResponse = createScheduleApiSchema.validate(
    resp.responseBody,
    { abortEarly: true }
  );

  if (apiResponse.error) {
    return {
      errorMessage: resp.errorBody?.errorMessage ? resp.errorBody?.errorMessage : apiResponse.error.message,
      isError: true,
    };
  }

  return {
    version: resp.responseBody?.version,
    jobId: resp.responseBody?.jobId,
    jobName: resp.responseBody?.jobName,
    jobType: resp.responseBody?.jobType,
    errorMessage: resp.errorBody?.errorMessage,
    isError: resp.isError
  };
};

export const deleteScheduler = async (journeyName: string, jobName: string) => {
  const resp = await apiWrapper.delete(`${process.env.REACT_APP_API_BASE_URL}/scheduler/schedule-journey/journeyName/${journeyName}/jobName/${jobName}`);

  const apiResponse = deleteSchedulerSchema.validate(
    resp.statusCode,
    { abortEarly: true }
  );

  if (apiResponse.value !== 204) {
    return undefined;
  }

  return resp.responseBody === '';
};

export const getScheduleList = async (getScheduleListRequest: IGetJobsRequest, page: number, pageSize: number, sortParameter: string) => {
  const resp = await apiWrapper.post<IGetJobsResponse, { errorMessage: string }>(`${process.env.REACT_APP_API_BASE_URL}/scheduler/schedule-journey/fetchAllJobs?page=${page}&size=${pageSize}&sort=${sortParameter}`, getScheduleListRequest);

  if (Object.keys(resp.responseBody ?? '').length === 0 && resp.statusCode === 404) {
    return {
      totalJobCount: 0,
      jobDetails: [],
      isError: false,
      statusCode: 200
    };
  }

  const apiResponse = scheduledJobListApiSchema.validate(
    resp.responseBody,
    { abortEarly: true }
  );

  if (apiResponse.error) {
    return {
      totalJobCount: 0,
      jobDetails: [],
      isError: true,
      statusCode: 500
    };
  }

  return {
    jobDetails: resp.responseBody?.jobDetails ?? [],
    totalJobCount: resp.responseBody?.totalJobCount ?? 0,
    isError: resp.isError,
    statusCode: resp.statusCode
  };
};

/**
 * @method Approve/ReturnJourney
 * @description api function to send the journey for approval
 * @param journeyName, sendForApprovalBody
 * @returns journey
 */
export const changeJourneyStatus = async (journeyName: string, journeyVersion: number, sendForApprovalReqObj: SendForApprovalBody) => {
  const resp = await apiWrapper.patch<{ uuid: string }>(`${process.env.REACT_APP_API_BASE_URL}/journeys/${journeyName}/version/${journeyVersion}`, sendForApprovalReqObj);
  // Schema validation for changeJourneyStatus api
  const apiResponse = updateStatusAPISchema.validate(
    resp.responseBody,
    { abortEarly: true }
  );

  if (apiResponse.error) {
    return undefined;
  }

  return resp.responseBody;
};

export const getEventSchema = async (eventName: string, eventVersion: number) => {
  const resp = await apiWrapper.get<EventSchemaDetail[]>(`${process.env.REACT_APP_API_BASE_URL}/events/${eventName}/version/${eventVersion}`);

  const apiResponse = eventSchemaDetailSchema.validate(
    resp.responseBody,
    { abortEarly: true }
  );
  if (apiResponse.error) {
    return undefined;
  }
  if (resp.responseBody && resp.responseBody.length > 0) {
    const firstElement = resp.responseBody[0];
    return firstElement && firstElement.schema ? firstElement : undefined;
  }
  return undefined;
};
